import { getApp } from "firebase/app";
import {
  Firestore,
  addDoc,
  collection,
  getDocs,
  getFirestore,
  limit,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

import {
  identifierToType,
  inviteErr,
  inviteOk,
  normalizeEmailLookup,
  normalizeUsernameLookup,
  type InviteResult,
  type SendGroupInvitationInput,
} from "./groupInvite.shared";

export type {
  InviteErrorCode,
  InviteIdentifier,
  InviteResult,
  SendGroupInvitationInput,
} from "./groupInvite.shared";

// Look up a user by the chosen identifier and return their uid + a snapshot
// of the identity fields we want to denormalize onto the invitation doc.
// Returns null when no match. `phoneNumber.verified === false` is intentionally
// not filtered — verification is orthogonal to identity, and delivery happens
// via an in-app listener, not SMS.
const lookupUser = async (
  firestore: Firestore,
  identifier: SendGroupInvitationInput["identifier"],
) => {
  const usersRef = collection(firestore, "users");

  let q;
  if (identifier.type === "email") {
    q = query(
      usersRef,
      where("email", "==", normalizeEmailLookup(identifier.value)),
      limit(2),
    );
  } else if (identifier.type === "username") {
    q = query(
      usersRef,
      where("username", "==", normalizeUsernameLookup(identifier.value)),
      limit(2),
    );
  } else {
    q = query(
      usersRef,
      where("phoneNumber.prefix", "==", identifier.prefix),
      where("phoneNumber.number", "==", identifier.number),
      limit(2),
    );
  }

  const snap = await getDocs(q);
  if (snap.empty) return null;

  if (snap.size > 1) {
    console.warn(
      `[groupInvite] More than one user matched a ${identifier.type} identifier; picking the first.`,
      snap.docs.map((d) => d.id),
    );
  }

  const doc = snap.docs[0];
  const data = doc.data() as {
    email?: string | null;
    username?: string | null;
    phoneNumber?: { prefix?: string | null; number?: string | null } | null;
  };
  return {
    uid: doc.id,
    email: data.email ?? null,
    username: data.username ?? null,
    phoneNumber:
      data.phoneNumber &&
      data.phoneNumber.prefix &&
      data.phoneNumber.number
        ? {
            prefix: data.phoneNumber.prefix,
            number: data.phoneNumber.number,
          }
        : null,
  };
};

export const sendGroupInvitationWith = async (
  firestore: Firestore,
  input: SendGroupInvitationInput,
): Promise<InviteResult> => {
  const { groupId, groupName, inviterUid, memberIds, identifier } = input;

  const target = await lookupUser(firestore, identifier);
  if (!target) return inviteErr("USER_NOT_FOUND");
  if (target.uid === inviterUid) return inviteErr("CANNOT_INVITE_YOURSELF");
  if (memberIds.includes(target.uid)) return inviteErr("USER_ALREADY_MEMBER");

  const invitesRef = collection(firestore, "groupInvitations");
  const dupSnap = await getDocs(
    query(
      invitesRef,
      where("groupId", "==", groupId),
      where("inviteeUid", "==", target.uid),
      where("status", "==", "pending"),
      limit(1),
    ),
  );
  if (!dupSnap.empty) return inviteErr("USER_ALREADY_INVITED");

  try {
    const docRef = await addDoc(invitesRef, {
      groupId,
      groupName,
      inviterId: inviterUid,
      inviteeUid: target.uid,
      inviteeIdentifierType: identifierToType(identifier),
      inviteeEmail: target.email,
      inviteeUsername: target.username,
      inviteePhone: target.phoneNumber,
      status: "pending",
      createdAt: serverTimestamp(),
    });
    return inviteOk(docRef.id, target.uid);
  } catch (err) {
    console.error("[groupInvite] addDoc failed", err);
    return inviteErr("INVITE_WRITE_FAILED");
  }
};

export const sendGroupInvitation = (input: SendGroupInvitationInput) =>
  sendGroupInvitationWith(getFirestore(getApp()), input);
